<script>
import { mapGetters } from 'vuex'

// FIXME: use another transition animation
import Fade from 'linna/components/Fade'

import Led from '../snippets/Led'
import StyledButton from '../snippets/StyledButton'

import MapPoint from './MapPoint'

import config from '@/config'

import getMapCoordinateLabelX from '@/util/getMapCoordinateLabelX'
import getMapCoordinateLabelY from '@/util/getMapCoordinateLabelY'

// FIXME: define visible items in props, so this can be used both in-game and for debugging

export default {
  name: 'GodMap',

  components: {
    Fade,
    Led,
    MapPoint,
    StyledButton
  },

  data () {
    return {
      maxMapX: config.maxMapX,
      maxMapY: config.maxMapY,
      cellWidth: 2,
      cellsOnScreen: 10,
      offsetX: 0,
      offsetY: 0
    }
  },

  computed: {

    ...mapGetters([
      'mapCellsByRow'
    ]),

    // Cell data

    visibleCellsByRow () {
      const rows = this.$store.getters.mapCellsByRow.slice(this.offsetY, this.maxY)

      return rows.map((cells) => {
        return cells.slice(this.offsetX, this.maxX)
      })
    },

    // Coordinates

    maxX () {
      return Math.min(this.maxMapX, (this.offsetX + this.cellsOnScreen))
    },

    maxY () {
      return Math.min(this.maxMapY, (this.offsetY + this.cellsOnScreen))
    },

    canDecreaseOffsetX () {
      return this.offsetX > 0
    },

    canIncreaseOffsetX () {
      return this.offsetX < (this.maxMapX - this.cellsOnScreen)
    },

    canDecreaseOffsetY () {
      return this.offsetY > 0
    },

    canIncreaseOffsetY () {
      return this.offsetY < (this.maxMapY - this.cellsOnScreen)
    },

    // LEDs

    maxLedX () {
      return Math.ceil(this.maxMapX / this.cellsOnScreen)
    },

    maxLedY () {
      return Math.ceil(this.maxMapY / this.cellsOnScreen)
    }

  },

  methods: {
    getMapCoordinateLabelX,
    getMapCoordinateLabelY,

    decreaseOffsetY () {
      this.offsetY = Math.max(0, (this.offsetY - this.cellsOnScreen))
    },

    decreaseOffsetX () {
      this.offsetX = Math.max(0, (this.offsetX - this.cellsOnScreen))
    },

    increaseOffsetY () {
      this.offsetY = Math.min((this.maxMapY - this.cellsOnScreen), (this.offsetY + this.cellsOnScreen))
    },

    increaseOffsetX () {
      this.offsetX = Math.min((this.maxMapX - this.cellsOnScreen), (this.offsetX + this.cellsOnScreen))
    }

  }

}
</script>

<template>
  <div class="c-god-map">
    <div class="c-god-map-content">

      <div class="c-god-map-controls">

        <!-- Map controls -->

        <div class="c-god-map-controls-top">

          <styled-button
            shortcut="arrowup"
            :disabled="!canDecreaseOffsetY"
            @click="decreaseOffsetY"
          >
            ⬆
          </styled-button>

        </div>

        <!-- Map global position -->

        <div class="c-god-map-controls-center">

          <styled-button
            shortcut="arrowleft"
            :disabled="!canDecreaseOffsetX"
            @click="decreaseOffsetX"
          >
            ⬅
          </styled-button>

          <div class="c-god-map-leds">

            <div
              v-for="y in maxLedY"
              :key="y"
              class="c-god-map-led-row"
            >
              <led
                v-for="x in maxLedX"
                :key="x"
                :on="(
                  Math.floor(offsetY / cellsOnScreen) === (y - 1) &&
                  Math.floor(offsetX / cellsOnScreen) === (x - 1)
                )"
              />
            </div>

          </div>

          <styled-button
            shortcut="arrowright"
            :disabled="!canIncreaseOffsetX"
            @click="increaseOffsetX"
          >
            ⮕
          </styled-button>

        </div>

        <div class="c-god-map-controls-bottom">

          <styled-button
            shortcut="arrowdown"
            :disabled="!canIncreaseOffsetY"
            @click="increaseOffsetY"
          >
            ⬇
          </styled-button>

        </div>

      </div>

      <!-- Map -->

      <div
        :key="cellsOnScreen + '-' + offsetX + '-' + offsetY + '-'"
        class="c-god-map-grid"
      >

        <div class="c-god-map-cells">

          <!-- X axis legend -->
          <div class="c-god-map-row">

            <div class="c-god-map-cell" />

            <div
              v-for="cell in visibleCellsByRow[0]"
              :key="cell.x"
              class="c-god-map-cell c-god-map-legend c-god-map-legend-x"
            >
              <div class="c-god-map-cell-content">
                {{ getMapCoordinateLabelX(cell.x) }}
              </div>
            </div>
          </div>

          <!-- Each row (Y) -->
          <div
            v-for="cells in visibleCellsByRow"
            :key="cells[0].y"
            class="c-god-map-row"
          >

            <!-- Y axis legend -->
            <div class="c-god-map-cell c-god-map-legend c-god-map-legend-y">
              <div class="c-god-map-cell-content">
                {{ getMapCoordinateLabelY(cells[0].y) }}
              </div>
            </div>

            <!-- Each cell on this row -->
            <div
              v-for="cell in cells"
              :key="cell.x"
              :class="{
                'c-god-map-cell-x-major': !((cell.x % 5) - 4),
                'c-god-map-cell-x-minor': !!((cell.x % 5) - 4),
                'c-god-map-cell-y-major': !((cell.y % 5) - 4),
                'c-god-map-cell-y-minor': !!((cell.y % 5) - 4)
              }"
              class="c-god-map-cell c-god-map-cell-coordinate"
            >

              <!-- Cell content -->
              <div class="c-god-map-cell-content">
                <fade>
                  <map-point
                    :cell="cell"
                    :key="cell.key"
                  />
                </fade>
              </div>

            </div>

          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<style lang="scss">

$c-god-map-cells-on-screen: 10;
$c-god-map-cell-width: 2;
$c-god-map-width: #{($c-god-map-cells-on-screen + 1) * $c-god-map-cell-width}rem;
$c-god-map-height: #{($c-god-map-cells-on-screen + 1) * $c-god-map-cell-width}rem;

.c-god-map {
  @include keep-center;
}

.c-god-map-content {
  @include flex;
  @include flex-center;
}

// Controls

.c-god-map-controls {
  @include flex-fluid;
  order: 1;
}

.c-god-map-controls-top,
.c-god-map-controls-bottom {
  @include keep-center;

  .c-styled-button {
    margin-left: 5.4rem;
  }

}

.c-god-map-controls-center {
  @include flex;
  @include flex-center;
}

.c-god-map-led-row {
  @include flex;
}

// Map

.c-god-map-grid {
  @include no-overflow;
  @include relative;
  @include flex-fixed;
  @include push-loose-right;
  order: -1;
  width: $c-god-map-width;
  height: $c-god-map-height;
}

.c-god-map-cells {
  @include relative;
}

.c-god-map-row {
  @include flex;
}

// Cells

.c-god-map-cell {
  @include border-box;
  @include relative;
  width: $c-god-map-cell-width * 1rem;
  height: $c-god-map-cell-width * 1rem;
}

.c-god-map-legend {
  border-color: rgba($default-text-color, 0.6);

  .c-god-map-cell-content {
    @include keep-full-center;
  }

}

.c-god-map-legend-x {
  border-bottom-width: 1px;
}

.c-god-map-legend-y {
  border-right-width: 1px;
}

.c-god-map-cell-coordinate {
  border-color: rgba($default-text-color, 0.2);
  border-right-width: 1px;
  border-bottom-width: 1px;

  .c-god-map-cell-content {
    @include fill-relative;

    .c-map-point {
      @include fill-relative;
    }

  }

  &.c-god-map-cell-x-major {
    border-right-color: rgba($default-text-color, 0.6);
  }

  &.c-god-map-cell-y-major {
    border-bottom-color: rgba($default-text-color, 0.6);
  }

}

</style>
